// Shared query helpers for the per-engine object-body fetchers (Slice 17).
//
// Two entry points, for two very different shapes:
//
//   - `fetchListingRows` — the CATALOG feeds (`list*ParseableObjects` /
//     `list*ProceduralObjects`). Paged through `navPagedRows.ts` so they share
//     the schema snapshot's introspection lane and spend the build's shared
//     body-byte budget. Never ships a whole catalog as one inline result set.
//   - `fetchRows` — the SINGLE-OBJECT re-pull the DDL invalidator uses
//     (`fetch*ObjectBody`). One row, no paging to do, but still queued on the
//     introspection lane so a burst of `ALTER VIEW` refreshes cannot starve
//     the user's own query.

import type { DatabaseEngineId } from '@/core/engines';
import type { QueryExecutionResult } from '@/types/queryExecution';
import { runIntrospectionQuery } from '@/features/connections/services/language-services/introspectionSemaphore';
import type { PersistedObjectListing, QualifiedName } from '../../types';
import type { SqlEngine } from '@/features/sql-grammar/model';

import type { NavBodyBudget } from '../navIndexBudget';
import { fetchNavRowsPaged, type NavPagedRowsQuery } from './navPagedRows';

export type RowMap = Record<string, unknown>;

/**
 * Per-request cap for a single-object body re-pull. One row is expected; the
 * cap only exists so a catalog surprise cannot ship an unbounded result set.
 */
const NAV_OBJECT_BODY_MAX_ROWS = 1_000;

/**
 * What every per-engine catalog feed needs: the connection to read, plus the
 * build-scoped budget and cancellation flag threaded down from
 * `buildIndexForConnection`. Both are optional so a unit test (or the E2E
 * `navObjectBodyFetcher` seam) can call a feed with no build around it.
 */
export type NavListingFetchOptions = {
  readonly connectionId: string;
  readonly database: string;
  readonly budget?: NavBodyBudget;
  readonly shouldAbort?: () => boolean;
  /** See `navPagedRows.ts` — fired when a feed's listing lost a page. */
  readonly onIncomplete?: () => void;
  /** See `navPagedRows.ts` — the subset of `onIncomplete` that is a FAILED
   *  catalog query rather than a cap. */
  readonly onFetchFailed?: () => void;
};

function nextExecutionId(connectionId: string, label: string): string {
  return `nav-fetch-${label}-${connectionId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function runFetcherQuery(
  connectionId: string,
  database: string,
  sql: string,
  label: string,
): Promise<QueryExecutionResult> {
  if (!window.electronAPI?.executeQueryV2) {
    return { success: false, error: { message: 'executeQueryV2 is not available' } };
  }
  const executeQueryV2 = window.electronAPI.executeQueryV2;
  return runIntrospectionQuery(connectionId, () =>
    executeQueryV2({
      connectionId,
      database,
      sql,
      executionId: nextExecutionId(connectionId, label),
      options: { maxRows: NAV_OBJECT_BODY_MAX_ROWS },
    }),
  );
}

/** Returns the raw rows for the first result set. THROWS when the query
 *  failed (or the IPC bridge is missing): the DDL invalidator re-verifies
 *  objects against the catalog and must be able to tell "the query ran and
 *  found nothing" (`[]` → the object is gone) from "the query did not run"
 *  (keep the previous records). Every caller that only wants a best-effort
 *  body catches. */
export async function fetchRows(
  connectionId: string,
  database: string,
  sql: string,
  label: string,
): Promise<readonly RowMap[]> {
  const result = await runFetcherQuery(connectionId, database, sql, label);
  if (!result.success) {
    throw new Error(`[nav-fetch ${label}] ${result.error?.message ?? 'query failed'}`);
  }
  // A capped result is not the object's body — it is part of it. Refusing it
  // keeps the previous (complete) records; multi-row bodies (Oracle
  // `ALL_SOURCE`, one row per line) go through `fetchSourceRowsPaged` instead.
  if (result.stats?.truncated) {
    throw new Error(`[nav-fetch ${label}] result truncated at ${NAV_OBJECT_BODY_MAX_ROWS} rows`);
  }
  return (result.resultSets?.[0]?.rows ?? []) as readonly RowMap[];
}

/**
 * Page a SINGLE object's multi-row body — Oracle ships a routine as one
 * `ALL_SOURCE` row per line, so a long procedure is thousands of rows —
 * through the same paged feed the connection-wide build uses (`navPagedRows`:
 * 500-row pages behind a stable `ORDER BY`, the introspection lane, the
 * legacy un-paged fallback). `sql` carries no `ORDER BY`; `orderBy` is the
 * stable key (`LINE`). THROWS when the listing is not complete (a lost page,
 * a truncated fallback) so a partial body never replaces complete records.
 */
export async function fetchSourceRowsPaged(
  connectionId: string,
  database: string,
  engine: NavPagedRowsQuery['engine'],
  label: string,
  sql: string,
  orderBy: string,
): Promise<readonly RowMap[]> {
  let incomplete = false;
  const rows = await fetchNavRowsPaged({
    connectionId,
    database,
    engine,
    label,
    sql,
    orderBy,
    dialect: 'offset-fetch',
    bodyColumns: [],
    onIncomplete: () => {
      incomplete = true;
    },
  });
  if (incomplete) throw new Error(`[nav-fetch ${label}] source listing incomplete`);
  return rows;
}

/**
 * Page one catalog feed. `sql` is the base statement (no `ORDER BY`, no page
 * tail); `orderBy` is the engine's stable total-order key and `bodyColumns`
 * the columns whose text the budget charges for.
 */
export async function fetchListingRows(
  options: NavListingFetchOptions,
  query: Pick<NavPagedRowsQuery, 'engine' | 'label' | 'sql' | 'orderBy' | 'dialect' | 'bodyColumns'>,
): Promise<readonly RowMap[]> {
  return fetchNavRowsPaged({
    ...query,
    connectionId: options.connectionId,
    database: options.database,
    budget: options.budget,
    shouldAbort: options.shouldAbort,
    onIncomplete: options.onIncomplete,
    onFetchFailed: options.onFetchFailed,
  });
}

export function escapeSqlLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

/** How the catalog stores an identifier the user typed: a delimited one as
 *  spelled; an undelimited one in the engine's own case — Oracle upper,
 *  PostgreSQL lower, the others as typed (SQL Server and SQLite compare
 *  case-insensitively; MySQL stores the typed case). */
export function catalogNameSpelling(engineId: DatabaseEngineId, name: string, quoted: boolean): string {
  if (quoted) return name;
  if (engineId === 'oracle') return name.toUpperCase();
  if (engineId === 'pgsql') return name.toLowerCase();
  return name;
}

export function asString(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

export function makeQualifiedName(schema: string, name: string): QualifiedName {
  return {
    schema: schema || undefined,
    name,
    raw: schema ? `${schema}.${name}` : name,
    quoted: false,
    foldedName: name.toLowerCase(),
    foldedSchema: schema ? schema.toLowerCase() : undefined,
  };
}

export function makeListing(
  engine: SqlEngine,
  objectKind: PersistedObjectListing['objectKind'],
  schema: string,
  name: string,
  bodyText: string,
): PersistedObjectListing {
  return {
    objectKind,
    objectName: makeQualifiedName(schema, name),
    bodyText,
    parseOptions: { engine },
  };
}
